/// <reference types="vite/client" />
import { checkPermission, Permission, type UserGroups } from '@metron/shared';

/**
 * In-app documentation. Each `*.md` file in this folder is one doc, bundled
 * at build time. The slug is the filename; title, description and the
 * optional gate come from a small frontmatter block:
 *
 *   ---
 *   title: Roles and Permissions
 *   description: Who can see and do what.
 *   requiredPermission: VIEW_USERS   (a Permission enum key; omit for everyone)
 *   ---
 *
 * Gating is about relevance, not confidentiality: the markdown ships in the
 * JS bundle, so never put anything here a signed-in Viewer shouldn't read.
 */
export interface DocEntry {
  slug: string;
  title: string;
  description: string;
  requiredPermission?: Permission;
  body: string;
}

const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

export function parseDoc(slug: string, source: string): DocEntry {
  const match = FRONTMATTER.exec(source);
  if (!match) throw new Error(`docs/${slug}.md is missing a frontmatter block`);
  const fields: Record<string, string> = {};
  for (const line of match[1].split(/\r?\n/)) {
    const idx = line.indexOf(':');
    if (idx > 0) fields[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
  }
  if (!fields.title) throw new Error(`docs/${slug}.md frontmatter needs a title`);

  let requiredPermission: Permission | undefined;
  if (fields.requiredPermission) {
    if (!(fields.requiredPermission in Permission)) {
      throw new Error(
        `docs/${slug}.md: unknown requiredPermission "${fields.requiredPermission}"`,
      );
    }
    requiredPermission = Permission[fields.requiredPermission as keyof typeof Permission];
  }

  return {
    slug,
    title: fields.title,
    description: fields.description ?? '',
    requiredPermission,
    body: source.slice(match[0].length),
  };
}

const sources = import.meta.glob('./*.md', { query: '?raw', import: 'default', eager: true });

export const docs: DocEntry[] = Object.entries(sources)
  .map(([path, source]) => {
    // `?raw` makes Vite import the file as a string; guard so widening the
    // glob to a non-raw asset fails loudly instead of parsing garbage.
    if (typeof source !== 'string') throw new Error(`docs: ${path} did not load as text`);
    return parseDoc(path.replace(/^\.\//, '').replace(/\.md$/, ''), source);
  })
  .sort((a, b) => a.title.localeCompare(b.title));

export function canViewDoc(doc: DocEntry, userGroups: UserGroups): boolean {
  return !doc.requiredPermission || checkPermission(userGroups, doc.requiredPermission);
}

export function visibleDocs(userGroups: UserGroups): DocEntry[] {
  return docs.filter((doc) => canViewDoc(doc, userGroups));
}

export function findDoc(slug: string | undefined): DocEntry | undefined {
  return docs.find((doc) => doc.slug === slug);
}
